import { Button, Input, Mentions, Tag } from "@arco-design/web-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react";

import {
  CONFIRMATION_ENTRY_RESOURCE_TYPES,
  CONFIRMATION_LINKED_RESOURCE_TYPES,
  VISIBLE_CONFIRMATION_ENTRY_RESOURCE_TYPES,
  parseConfirmationEntries,
  type ConfirmationEntry,
  type ConfirmationEntryResourceType,
  type ConfirmationItineraryDay,
} from "../service/confirmationEntryParser";
import { fulfillmentSupplierOptionLabel } from "../service";
import type {
  FulfillmentResourceMentionCandidate,
  FulfillmentResourceMentionLevel,
  FulfillmentResourceMentionType,
  FulfillmentResourceTransportMode,
  FulfillmentTransportResourceCandidate,
  FulfillmentTransportPoint,
} from "../types";

/** 清理住宿候选的地区与地址说明，避免重复显示酒店名称或空分隔符。 */
export function fulfillmentStayCandidateMeta(candidate: FulfillmentResourceMentionCandidate): string {
  const hotelName = candidate.spuName?.trim() ?? "";
  return (candidate.description ?? "")
    .split("·")
    .map((part) => part.trim())
    .filter((part) => Boolean(part) && part !== hotelName)
    .join(" · ");
}

/** 住宿下拉候选采用酒店名称与地区地址两行展示。 */
export function FulfillmentStayCandidateOption({
  candidate,
}: {
  candidate: FulfillmentResourceMentionCandidate;
}) {
  const meta = fulfillmentStayCandidateMeta(candidate);
  return (
    <span className="fulfillment-order-stay-option">
      <span className="fulfillment-order-stay-option__name">{candidate.spuName}</span>
      {meta ? <span className="fulfillment-order-stay-option__meta">{meta}</span> : null}
    </span>
  );
}

/** 供应商候选只展示可读业务资料，不暴露内部编号。 */
export function FulfillmentSupplierOption({ supplier }: { supplier: import("../types").FulfillmentSupplierOption }) {
  const [businessName, ...metaParts] = fulfillmentSupplierOptionLabel(supplier).split(" · ");
  const meta = metaParts.join(" · ");
  return <span className="fulfillment-order-supplier-option"><span className="fulfillment-order-supplier-option__name">{businessName || supplier.name}</span>{meta ? <span className="fulfillment-order-supplier-option__meta">{meta}</span> : null}</span>;
}

/** 按点位查询结果说明缺少或重名的机场、火车站。 */
function transportPointMatchError(mode: FulfillmentResourceTransportMode, name: string, count: number): string {
  const kind = mode === "air" ? "机场" : "火车站";
  return count === 0
    ? `没有“${name}”${kind}，请检查名称或从候选中重新选择`
    : `找到多个“${name}”${kind}，请从候选中明确选择`;
}

const EXAMPLES: Record<ConfirmationEntryResourceType, readonly [string, string]> = {
  住宿: [
    "2-4 @标间 24x180;@三人间2x200;@大床房住老师2x250",
    "5,6 @大床房住两人5x190;@标间22x180;@大床房1x0;@大床房住老师1x250；7-9@标间 24x180;@三人间2x200",
  ],
  大交通: [
    "飞机 @北京大兴国际机场-@大理凤仪机场 46x1090",
    "火车 @西昌西站-@重庆北站 #二等座 46张 550元",
  ],
  包车: [
    "1,7 接送 @37座 2x1200，@47座 1x1800",
    "4-7 包车 @37座 2x8200，@49座 1x9800，@55座 1x10500",
  ],
  考察点: [
    "@三坊七巷 #三坊七巷 40x2x50; #三坊七巷老师全价票 2x1x80",
    "@三坊七巷 学生 46人 50元",
  ],
  餐: [
    "@上海 学生 晚餐 5x50, 中餐4x60; 老师 正餐 1x8x80,1x9x100",
    "@杭州 学生 早餐 8x10",
  ],
  导游: [
    "2-3 @导游工资 300, @考察团补贴 20, @路补 100, @餐补 60",
    "1-7 全程导服 1人 280元",
  ],
  杂费: ["快递费 D1 1项 500元", "企业资金池 50人 5元"],
};

type Props = {
  disabled?: boolean;
  /** 同页切换业务时保留草稿与资源缓存，但不输出可聚焦控件。 */
  suspended?: boolean;
  selectedStaySpu?: FulfillmentResourceMentionCandidate | null;
  subjectSlot?: ReactNode;
  afterEntrySlot?: ReactNode;
  itineraryDays?: readonly ConfirmationItineraryDay[];
  searchResourceCandidates?: (
    resourceType: FulfillmentResourceMentionType,
    level: FulfillmentResourceMentionLevel,
    keyword: string,
    spuId?: string | null,
    transportMode?: FulfillmentResourceTransportMode | null,
    fulfillmentSupplierId?: string | null,
  ) => Promise<FulfillmentResourceMentionCandidate[]>;
  searchTransportPoints?: (
    mode: FulfillmentResourceTransportMode,
    keyword: string,
  ) => Promise<FulfillmentTransportPoint[]>;
  searchTransportResourceCandidates?: (
    mode: FulfillmentResourceTransportMode,
    fromPointId: string,
    toPointId: string,
    specKeyword?: string | null,
  ) => Promise<FulfillmentTransportResourceCandidate[]>;
  onResourceTypeChange?: (resourceType: ConfirmationEntryResourceType) => void;
  onPendingTextChange?: (pending: boolean) => void;
  onAdd: (entries: ConfirmationEntry[]) => void | Promise<void>;
};

type MentionSearchTarget = {
  level: FulfillmentResourceMentionLevel;
  spuId: string | null;
  transportMode: FulfillmentResourceTransportMode | null;
};

type MentionOption = {
  value: string;
  label: ReactNode;
};

type TransportPointWithMode = FulfillmentTransportPoint & {
  mode: FulfillmentResourceTransportMode;
};

/** 当前大交通片段中已经输入的起终点与席别。 */
type TransportMentionContext = {
  mode: FulfillmentResourceTransportMode;
  fromName: string;
  toName: string;
  seatClass: string | null;
};

/** 提取片段起点前显式声明的大交通方式。 */
function explicitTransportMode(segment: string): FulfillmentResourceTransportMode | null {
  const endpointStart = segment.indexOf("@");
  const prefix = endpointStart >= 0 ? segment.slice(0, endpointStart) : segment;
  const modeToken = [...prefix.matchAll(/(?:^|\s)(飞机|火车)(?=\s|$)/gu)].at(-1)?.[1];
  return modeToken === "飞机" ? "air" : modeToken === "火车" ? "rail" : null;
}

/** 从单个输入片段提取大交通候选查询上下文。 */
function transportMentionContextFromSegment(
  segment: string,
  mode: FulfillmentResourceTransportMode,
): TransportMentionContext | null {
  const fromAt = segment.indexOf("@");
  const dash = fromAt >= 0 ? segment.indexOf("-", fromAt) : -1;
  const toAt = dash >= 0 ? segment.indexOf("@", dash) : -1;
  if (fromAt < 0 || dash < 0 || toAt < 0) return null;
  const fromName = segment.slice(fromAt + 1, dash).trim();
  const toAndSpec = segment.slice(toAt + 1).trim();
  const seatMarker = toAndSpec.indexOf("#");
  const toPart = seatMarker >= 0 ? toAndSpec.slice(0, seatMarker) : toAndSpec;
  const quantityMarker = toPart.search(/\s+\d+(?:\.\d+)?\s*(?:x|X|×|\*|张)(?=\s*\d|\s*$)/u);
  const toName = (quantityMarker >= 0 ? toPart.slice(0, quantityMarker) : toPart).trim();
  const seatClass = seatMarker >= 0
    ? toAndSpec.slice(seatMarker + 1).split(/\s+\d/u)[0]?.trim() || null
    : null;
  return fromName && toName ? { mode, fromName, toName, seatClass } : null;
}

/** 按输入顺序提取路线，并让未重复声明方式的后续片段继承最近方式。 */
function transportMentionContexts(text: string): TransportMentionContext[] {
  let inheritedMode: FulfillmentResourceTransportMode | null = null;
  const contexts: TransportMentionContext[] = [];
  for (const rawSegment of text.split(/[;；\r\n]/u)) {
    const segment = rawSegment.trim();
    inheritedMode = explicitTransportMode(segment) ?? inheritedMode;
    if (!inheritedMode) continue;
    const context = transportMentionContextFromSegment(segment, inheritedMode);
    if (context) contexts.push(context);
  }
  return contexts;
}

/** 提取已经包含数量和单价、且只用一个 @ 的考察点同名 SKU。 */
function pointGlobalSkuNames(text: string): string[] {
  const names: string[] = [];
  for (const rawSegment of text.split(/[;；\r\n]/u)) {
    const segment = rawSegment.trim();
    if (!segment || segment.includes("#")) continue;
    const mentions = [...segment.matchAll(/@([^@#;；,，\r\n]+?)(?=\s|\d|$)/gu)];
    if (mentions.length !== 1) continue;
    const hasCompletePrice = (
      /\d+(?:\.\d+)?\s*[xX×*]\s*\d+(?:\.\d+)?\s*元?\s*$/u.test(segment)
      || /\d+(?:\.\d+)?\s*(?:人|位|个|张|份|项|团)\s+\d+(?:\.\d+)?\s*元?\s*$/u.test(segment)
    );
    const name = mentions[0]?.[1]?.trim();
    if (hasCompletePrice && name) names.push(name);
  }
  return [...new Set(names)];
}

/** 判断当前分类是否开放资源关联。 */
function isMentionResourceType(
  resourceType: ConfirmationEntryResourceType,
): resourceType is FulfillmentResourceMentionType {
  return (CONFIRMATION_LINKED_RESOURCE_TYPES as readonly string[]).includes(resourceType);
}

/** 使用资源身份生成稳定缓存键。 */
function candidateCacheKey(candidate: FulfillmentResourceMentionCandidate): string {
  return [
    candidate.resourceType,
    candidate.level,
    candidate.spuId ?? "",
    candidate.skuId ?? "",
    candidate.skuName ?? candidate.vehicleSpecName ?? candidate.mentionText,
  ].join(":");
}

/** 找出输入范围内最后一次选中的 SPU。 */
function latestMentionedSpu(
  scope: string,
  resourceType: FulfillmentResourceMentionType,
  candidates: readonly FulfillmentResourceMentionCandidate[],
): FulfillmentResourceMentionCandidate | null {
  let matched: {
    candidate: FulfillmentResourceMentionCandidate;
    index: number;
    tokenLength: number;
  } | null = null;
  for (const candidate of candidates) {
    if (candidate.resourceType !== resourceType) continue;
    const token = `@${candidate.mentionText}`;
    let index = scope.lastIndexOf(token);
    while (index >= 0) {
      const nextCharacter = scope[index + token.length];
      if (!nextCharacter || /[\s;；]/u.test(nextCharacter)) break;
      index = scope.lastIndexOf(token, index - 1);
    }
    if (
      index >= 0
      && (
        !matched
        || index > matched.index
        || (index === matched.index && token.length > matched.tokenLength)
      )
    ) {
      matched = { candidate, index, tokenLength: token.length };
    }
  }
  return matched?.candidate ?? null;
}

/** 包车使用后端车型候选，其余 Mentions 类型使用完整资源候选。 */
function usesMentionsInput(resourceType: ConfirmationEntryResourceType): boolean {
  return resourceType === "包车" || isMentionResourceType(resourceType);
}

/** 从完整包车文本提取已输入完成的座位规格，支持直接粘贴后自动关联候选。 */
function busVehicleSpecNames(text: string): string[] {
  const normalized = text.normalize("NFKC");
  return [...new Set(
    [...normalized.matchAll(/@([1-9]\d{0,2})\s*座(?=\s|\d|[,，;；\r\n]|$)/gu)]
      .map((match) => `${Number(match[1])}座`),
  )];
}

/** 从完整住宿文本提取房型名称，支持直接粘贴后自动关联当前酒店的现有规格。 */
function stayRoomSkuNames(text: string): string[] {
  const normalized = text.normalize("NFKC");
  return [...new Set(
    [...normalized.matchAll(/@([^\s\d@#;；,，\r\n]+?)(?=(?:住(?:两人|老师|教师|学生|导游|司机))?\s*\d)/gu)]
      .map((match) => match[1]?.trim())
      .filter((name): name is string => Boolean(name)),
  )];
}

/** 根据当前 @ 之前已选中的 SPU，决定本次查询 SPU 还是其下 SKU。 */
function inferMentionSearchTarget(
  resourceType: FulfillmentResourceMentionType,
  text: string,
  caret: number,
  candidates: readonly FulfillmentResourceMentionCandidate[],
): MentionSearchTarget {
  const beforeCaret = text.slice(0, Math.max(0, caret));
  const mentionIndex = beforeCaret.lastIndexOf("@");
  const beforeMention = mentionIndex >= 0 ? beforeCaret.slice(0, mentionIndex) : beforeCaret;
  const segmentStart = Math.max(
    beforeMention.lastIndexOf(";"),
    beforeMention.lastIndexOf("；"),
    beforeMention.lastIndexOf("\n"),
    beforeMention.lastIndexOf("\r"),
  ) + 1;
  const currentSegment = beforeMention.slice(segmentStart);
  const transportMode = resourceType === "大交通"
    ? [...currentSegment.matchAll(/(?:^|\s)(飞机|火车)(?=\s|$)/gu)].at(-1)?.[1] ?? null
    : null;
  const normalizedTransportMode = transportMode === "飞机"
    ? "air"
    : transportMode === "火车"
      ? "rail"
      : null;
  const spuScope = resourceType === "住宿" ? beforeMention : currentSegment;
  const spu = latestMentionedSpu(spuScope, resourceType, candidates);
  if (!spu) return { level: "spu", spuId: null, transportMode: normalizedTransportMode };

  const alreadySelectedSku = candidates.some((candidate) => (
    candidate.resourceType === resourceType
    && candidate.level === "sku"
    && candidate.spuId === spu.spuId
    && currentSegment.includes(`@${candidate.mentionText}`)
  ));
  return alreadySelectedSku
    ? { level: "spu", spuId: null, transportMode: normalizedTransportMode }
    : { level: "sku", spuId: spu.spuId, transportMode: normalizedTransportMode };
}

/** 根据当前输入片段推断大交通方式。 */
function transportModeFromText(text: string): FulfillmentResourceTransportMode | null {
  let mode: FulfillmentResourceTransportMode | null = null;
  for (const segment of text.split(/[;；\r\n]/u)) {
    mode = explicitTransportMode(segment.trim()) ?? mode;
  }
  return mode;
}

/** 确认件域内独立录入，不复用核价候选和数量语义。 */
export function FulfillmentOrderQuickEntry({
  disabled,
  suspended = false,
  selectedStaySpu = null,
  subjectSlot,
  afterEntrySlot,
  itineraryDays = [],
  searchResourceCandidates,
  searchTransportPoints,
  onResourceTypeChange,
  onPendingTextChange,
  onAdd,
}: Props) {
  const [resourceType, setResourceType] = useState<ConfirmationEntryResourceType>("住宿");
  const [drafts, setDrafts] = useState<Record<ConfirmationEntryResourceType, string>>(() =>
    Object.fromEntries(CONFIRMATION_ENTRY_RESOURCE_TYPES.map((type) => [type, ""])) as Record<
      ConfirmationEntryResourceType,
      string
    >,
  );
  const [preview, setPreview] = useState<ConfirmationEntry[]>([]);
  const hasPendingText = Object.values(drafts).some((value) => value.trim().length > 0);
  useEffect(() => { onPendingTextChange?.(hasPendingText); }, [hasPendingText, onPendingTextChange]);
  const [error, setError] = useState("");
  const [mentionError, setMentionError] = useState("");
  const [exampleResolving, setExampleResolving] = useState(false);
  const [, setMentionCandidates] = useState<FulfillmentResourceMentionCandidate[]>([]);
  const [mentionOptions, setMentionOptions] = useState<MentionOption[]>([]);
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const mentionsRef = useRef<HTMLDivElement | null>(null);
  const candidateCacheRef = useRef(new Map<string, FulfillmentResourceMentionCandidate>());
  const transportPointCacheRef = useRef(new Map<string, TransportPointWithMode>());
  const resourceTypeRef = useRef(resourceType);
  const textRef = useRef("");
  const searchPortRef = useRef(searchResourceCandidates);
  const transportPointsPortRef = useRef(searchTransportPoints);
  const selectedStaySpuRef = useRef(selectedStaySpu);
  const previousStaySpuRef = useRef(selectedStaySpu);
  const staySelectionGenerationRef = useRef(0);
  const searchGenerationRef = useRef(0);
  const exampleGenerationRef = useRef(0);
  const searchKeyRef = useRef<string | null>(null);
  const mentionPhysicalInputRef = useRef(false);
  const mentionOptionSelectingRef = useRef(false);
  const mentionFallbackCaretRef = useRef(0);
  const mentionKeyupFallbackRef = useRef<number | null>(null);
  const mentionOptionSelectionResetRef = useRef<number | null>(null);
  const transportPointSearchKeyRef = useRef<string | null>(null);
  const pointResourceSearchKeyRef = useRef<string | null>(null);
  const busVehicleSpecSearchKeyRef = useRef<string | null>(null);
  const stayRoomSkuSearchKeyRef = useRef<string | null>(null);
  const mentionSearchDebounceRef = useRef<number | null>(null);
  const resourceResolutionDebounceRef = useRef<number | null>(null);
  const text = drafts[resourceType];
  resourceTypeRef.current = resourceType;
  textRef.current = text;
  searchPortRef.current = searchResourceCandidates;
  transportPointsPortRef.current = searchTransportPoints;
  selectedStaySpuRef.current = selectedStaySpu;
  const previewTotal = useMemo(
    () => preview.reduce((total, entry) => total + Number(entry.amount), 0),
    [preview],
  );

  /** 将当前识别结果加入明细，并清理输入状态。按钮放在标题动作区，便于录入后立即提交。 */
  async function handleAddPreview() {
    try {
      await onAdd(preview);
      if (resourceTypeRef.current === resourceType && textRef.current === text) {
        setError((current) => current === error ? "" : current);
      }
      setPreview([]);
      setDrafts((current) => ({ ...current, [resourceType]: "" }));
      textRef.current = "";
      searchGenerationRef.current += 1;
      searchKeyRef.current = null;
      busVehicleSpecSearchKeyRef.current = null;
      stayRoomSkuSearchKeyRef.current = null;
      setMentionCandidates([]);
      setMentionOptions([]);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "加入明细失败，请重试");
    }
  }

  useEffect(() => {
    /** 实体键盘已把光标推进到预期位置时取消兜底测量。 */
    function cancelMentionKeyupFallback(event: globalThis.KeyboardEvent) {
      const target = event.target;
      if (!(target instanceof Node) || !mentionsRef.current?.contains(target)) return;
      mentionPhysicalInputRef.current = false;
      const textarea = mentionsRef.current.querySelector("textarea");
      if (
        textarea?.selectionStart !== mentionFallbackCaretRef.current
        || textarea.selectionEnd !== mentionFallbackCaretRef.current
      ) return;
      if (mentionKeyupFallbackRef.current !== null) {
        window.clearTimeout(mentionKeyupFallbackRef.current);
        mentionKeyupFallbackRef.current = null;
      }
    }

    document.addEventListener("keyup", cancelMentionKeyupFallback, true);
    return () => {
      document.removeEventListener("keyup", cancelMentionKeyupFallback, true);
      if (mentionKeyupFallbackRef.current !== null) {
        window.clearTimeout(mentionKeyupFallbackRef.current);
      }
      if (mentionOptionSelectionResetRef.current !== null) {
        window.clearTimeout(mentionOptionSelectionResetRef.current);
      }
      if (mentionSearchDebounceRef.current !== null) {
        window.clearTimeout(mentionSearchDebounceRef.current);
      }
      if (resourceResolutionDebounceRef.current !== null) {
        window.clearTimeout(resourceResolutionDebounceRef.current);
      }
    };
  }, []);

  const updateDraftAndPreview = useCallback((nextText: string) => {
    setDrafts((current) => ({ ...current, [resourceType]: nextText }));
    if (!nextText.trim()) {
      setPreview([]);
      setError("");
      return;
    }
    if (resourceType === "住宿" && !selectedStaySpuRef.current?.spuId) {
      setPreview([]);
      setError("请先选择住宿酒店");
      return;
    }
    const result = parseConfirmationEntries({
      resourceType,
      text: nextText,
      itineraryDays,
      mentionCandidates: [...candidateCacheRef.current.values()],
      selectedStaySpu: selectedStaySpuRef.current,
      transportPoints: [...transportPointCacheRef.current.values()],
      requireLinkedMentions: true,
    });
    if (!result.ok) {
      setPreview([]);
      setError(resourceType === "大交通" && result.error.startsWith("没有") ? "" : result.error);
      return;
    }
    setError("");
    setPreview(result.entries);
  }, [itineraryDays, resourceType]);

  const scheduleMentionKeyupFallback = () => {
    if (mentionKeyupFallbackRef.current !== null) {
      window.clearTimeout(mentionKeyupFallbackRef.current);
    }
    mentionKeyupFallbackRef.current = window.setTimeout(() => {
      mentionKeyupFallbackRef.current = null;
      const textarea = mentionsRef.current?.querySelector("textarea");
      if (textarea && document.activeElement === textarea) {
        textarea.setSelectionRange(mentionFallbackCaretRef.current, mentionFallbackCaretRef.current);
        textarea.dispatchEvent(new globalThis.KeyboardEvent("keyup", {
          key: "Unidentified",
          bubbles: true,
        }));
      }
    });
  };

  /** 无候选弹层时阻止 Mentions 拦截 Enter，保留 textarea 原生换行。 */
  const handleEntryKeyDownCapture = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.nativeEvent.isComposing) {
      mentionPhysicalInputRef.current = true;
      event.stopPropagation();
      return;
    }
    const selectsByEnter = event.key === "Enter"
      && mentionOptions.length > 0
      && Boolean(document.querySelector('[role="listbox"] [role="option"], .arco-select-option'));
    if (selectsByEnter) {
      mentionOptionSelectingRef.current = true;
      if (mentionOptionSelectionResetRef.current !== null) {
        window.clearTimeout(mentionOptionSelectionResetRef.current);
      }
      mentionOptionSelectionResetRef.current = window.setTimeout(() => {
        mentionOptionSelectingRef.current = false;
        mentionOptionSelectionResetRef.current = null;
      }, 500);
      return;
    }
    mentionPhysicalInputRef.current = true;
    if (event.key === "Enter") {
      event.stopPropagation();
    }
  };

  const handleTextChange = (nextText: string) => {
    const previousText = textRef.current;
    textRef.current = nextText;
    transportPointSearchKeyRef.current = null;
    const isTransportOptionSelection = resourceType === "大交通" && mentionOptionSelectingRef.current;
    if (!isTransportOptionSelection) searchGenerationRef.current += 1;
    searchKeyRef.current = null;
    setMentionCandidates([]);
    setMentionOptions([]);
    setMentionError("");
    updateDraftAndPreview(nextText);
    if (resourceResolutionDebounceRef.current !== null) {
      window.clearTimeout(resourceResolutionDebounceRef.current);
    }
    resourceResolutionDebounceRef.current = window.setTimeout(() => {
      resourceResolutionDebounceRef.current = null;
      if (textRef.current !== nextText) return;
      void ensureBusVehicleSpecCandidates(nextText);
      void ensureStayRoomSkuCandidates(nextText);
      void ensureTransportPoints(nextText);
      void ensurePointGlobalSkuCandidates(nextText);
    }, 500);
    if (!usesMentionsInput(resourceType)) return;

    let commonPrefixLength = 0;
    while (
      commonPrefixLength < previousText.length
      && commonPrefixLength < nextText.length
      && previousText[commonPrefixLength] === nextText[commonPrefixLength]
    ) commonPrefixLength += 1;
    let commonSuffixLength = 0;
    while (
      commonSuffixLength < previousText.length - commonPrefixLength
      && commonSuffixLength < nextText.length - commonPrefixLength
      && previousText[previousText.length - commonSuffixLength - 1]
        === nextText[nextText.length - commonSuffixLength - 1]
    ) commonSuffixLength += 1;
    mentionFallbackCaretRef.current = nextText.length - commonSuffixLength;
    if (mentionOptionSelectingRef.current) {
      mentionOptionSelectingRef.current = false;
      return;
    }
    if (!mentionPhysicalInputRef.current) scheduleMentionKeyupFallback();
  };

  const ensureStayRoomSkuCandidates = useCallback(async (nextText: string) => {
    if (resourceTypeRef.current !== "住宿") return;
    const names = stayRoomSkuNames(nextText);
    const searchPort = searchPortRef.current;
    if (!names.length || !searchPort || !selectedStaySpuRef.current?.spuId) {
      stayRoomSkuSearchKeyRef.current = null;
      return;
    }
    const searchKey = JSON.stringify([selectedStaySpuRef.current.spuId, nextText]);
    if (stayRoomSkuSearchKeyRef.current === searchKey) return;
    stayRoomSkuSearchKeyRef.current = searchKey;
    const selectionGeneration = staySelectionGenerationRef.current;
    try {
      for (const name of names) {
        const candidates = await searchPort("住宿", "sku", name, null);
        if (
          stayRoomSkuSearchKeyRef.current !== searchKey
          || staySelectionGenerationRef.current !== selectionGeneration
          || resourceTypeRef.current !== "住宿"
          || searchPortRef.current !== searchPort
          || textRef.current !== nextText
        ) return;
        const matched = candidates.find((candidate) => (
          candidate.resourceType === "住宿"
          && candidate.level === "sku"
          && (candidate.skuName ?? candidate.mentionText) === name
        ));
        if (!matched) {
          setPreview([]);
          setMentionError(`当前没有启用的房型“${name}”，请从候选中重新选择`);
          return;
        }
        candidateCacheRef.current.set(candidateCacheKey(matched), matched);
      }
      setMentionError("");
      updateDraftAndPreview(textRef.current);
    } catch {
      if (stayRoomSkuSearchKeyRef.current !== searchKey
        || staySelectionGenerationRef.current !== selectionGeneration) return;
      stayRoomSkuSearchKeyRef.current = null;
      setPreview([]);
      setMentionError("住宿房型候选查询失败，请稍后重试");
    }
  }, [updateDraftAndPreview]);

  useEffect(() => {
    const previous = previousStaySpuRef.current;
    previousStaySpuRef.current = selectedStaySpu;
    if (previous?.spuId === selectedStaySpu?.spuId
      && previous?.spuName === selectedStaySpu?.spuName) return;
    staySelectionGenerationRef.current += 1;
    stayRoomSkuSearchKeyRef.current = null;
    for (const [key, candidate] of candidateCacheRef.current) {
      if (candidate.resourceType === "住宿") candidateCacheRef.current.delete(key);
    }
    if (resourceType !== "住宿") return;
    searchGenerationRef.current += 1;
    exampleGenerationRef.current += 1;
    searchKeyRef.current = null;
    setMentionCandidates([]);
    setMentionOptions([]);
    setMentionError("");
    setExampleResolving(false);
    if (mentionSearchDebounceRef.current !== null) {
      window.clearTimeout(mentionSearchDebounceRef.current);
      mentionSearchDebounceRef.current = null;
    }
    if (resourceResolutionDebounceRef.current !== null) {
      window.clearTimeout(resourceResolutionDebounceRef.current);
      resourceResolutionDebounceRef.current = null;
    }
    updateDraftAndPreview(textRef.current);
    void ensureStayRoomSkuCandidates(textRef.current);
  }, [ensureStayRoomSkuCandidates, resourceType, selectedStaySpu, updateDraftAndPreview]);

  const ensureBusVehicleSpecCandidates = async (nextText: string) => {
    if (resourceTypeRef.current !== "包车") return;
    const vehicleSpecs = busVehicleSpecNames(nextText);
    const searchPort = searchPortRef.current;
    if (!vehicleSpecs.length || !searchPort) {
      busVehicleSpecSearchKeyRef.current = null;
      return;
    }
    const searchKey = vehicleSpecs.join("|");
    if (busVehicleSpecSearchKeyRef.current === searchKey) return;
    busVehicleSpecSearchKeyRef.current = searchKey;
    try {
      for (const vehicleSpec of vehicleSpecs) {
        const candidates = await searchPort("包车", "sku", vehicleSpec, null);
        if (
          busVehicleSpecSearchKeyRef.current !== searchKey
          || resourceTypeRef.current !== "包车"
          || searchPortRef.current !== searchPort
        ) return;
        const matched = candidates.find((candidate) => (
          candidate.resourceType === "包车"
          && (candidate.vehicleSpecName ?? candidate.skuName ?? candidate.mentionText)
            .normalize("NFKC").replace(/\s+/gu, "") === vehicleSpec
        ));
        if (!matched) {
          setPreview([]);
          setMentionError(`当前没有启用的车型“${vehicleSpec}”，请从候选中重新选择`);
          return;
        }
        candidateCacheRef.current.set(candidateCacheKey(matched), matched);
      }
      setMentionError("");
      updateDraftAndPreview(textRef.current);
    } catch {
      if (busVehicleSpecSearchKeyRef.current !== searchKey) return;
      busVehicleSpecSearchKeyRef.current = null;
      setPreview([]);
      setMentionError("车型候选查询失败，请稍后重试");
    }
  };

  const ensureTransportPoints = async (nextText: string) => {
    if (resourceTypeRef.current !== "大交通") return;
    const contexts = transportMentionContexts(nextText);
    const pointsPort = transportPointsPortRef.current;
    if (!contexts.length || !pointsPort) {
      transportPointSearchKeyRef.current = null;
      return;
    }
    const searchKey = contexts.map((context) => (
      [context.mode, context.fromName, context.toName, context.seatClass ?? ""].join(":")
    )).join("|");
    if (transportPointSearchKeyRef.current === searchKey) return;
    transportPointSearchKeyRef.current = searchKey;
    try {
      for (const context of contexts) {
        let from = [...transportPointCacheRef.current.values()].find(
          (point) => point.mode === context.mode
            && (point.name === context.fromName || point.shortName === context.fromName),
        );
        let to = [...transportPointCacheRef.current.values()].find(
          (point) => point.mode === context.mode
            && (point.name === context.toName || point.shortName === context.toName),
        );
        if ((!from || !to) && pointsPort) {
          const [fromCandidates, toCandidates] = await Promise.all([
            from ? Promise.resolve([from]) : pointsPort(context.mode, context.fromName),
            to ? Promise.resolve([to]) : pointsPort(context.mode, context.toName),
          ]);
          if (transportPointSearchKeyRef.current !== searchKey) return;
          const exactFrom = fromCandidates.filter((point) => (
            point.name === context.fromName || point.shortName === context.fromName
          ));
          const exactTo = toCandidates.filter((point) => (
            point.name === context.toName || point.shortName === context.toName
          ));
          from = exactFrom.length === 1 ? { ...exactFrom[0]!, mode: context.mode } : undefined;
          to = exactTo.length === 1 ? { ...exactTo[0]!, mode: context.mode } : undefined;
          if (from) transportPointCacheRef.current.set(`${context.mode}:${from.name}`, from);
          if (to) transportPointCacheRef.current.set(`${context.mode}:${to.name}`, to);
          if (!from || !to) {
            transportPointSearchKeyRef.current = null;
            setPreview([]);
            setMentionError(transportPointMatchError(
              context.mode,
              !from ? context.fromName : context.toName,
              !from ? exactFrom.length : exactTo.length,
            ));
            return;
          }
        }
      }
      setMentionError("");
      updateDraftAndPreview(textRef.current);
    } catch {
      if (transportPointSearchKeyRef.current !== searchKey) return;
      transportPointSearchKeyRef.current = null;
      setPreview([]);
      setMentionError("交通点位候选查询失败，请稍后重试");
    }
  };

  const ensurePointGlobalSkuCandidates = async (nextText: string) => {
    if (resourceTypeRef.current !== "考察点") return;
    const names = pointGlobalSkuNames(nextText);
    const searchPort = searchPortRef.current;
    if (!names.length || !searchPort) {
      pointResourceSearchKeyRef.current = null;
      return;
    }
    const searchKey = names.join("|");
    if (pointResourceSearchKeyRef.current === searchKey) return;
    pointResourceSearchKeyRef.current = searchKey;
    try {
      for (const name of names) {
        const candidates = await searchPort("考察点", "sku", name, null);
        if (
          pointResourceSearchKeyRef.current !== searchKey
          || resourceTypeRef.current !== "考察点"
          || searchPortRef.current !== searchPort
        ) return;
        const exact = [...new Map(candidates.filter((candidate) => (
          candidate.resourceType === "考察点"
          && candidate.level === "sku"
          && candidate.skuName === name
          && Boolean(candidate.spuId)
          && Boolean(candidate.skuId)
        )).map((candidate) => [candidateCacheKey(candidate), candidate])).values()];
        if (exact.length !== 1) {
          setPreview([]);
          setMentionError(exact.length === 0
            ? `未找到考察点“${name}”的同名 SKU，请从候选中重新选择`
            : `考察点“${name}”存在多个同名 SKU，请从候选中明确选择`);
          return;
        }
        candidateCacheRef.current.set(candidateCacheKey(exact[0]!), exact[0]!);
      }
      setMentionError("");
      updateDraftAndPreview(textRef.current);
    } catch {
      if (pointResourceSearchKeyRef.current !== searchKey) return;
      pointResourceSearchKeyRef.current = null;
      setMentionError("考察点资源候选查询失败，请稍后重试");
    }
  };

  const handleMentionSearch = async (keyword: string, prefix = "@") => {
    const currentResourceType = resourceTypeRef.current;
    const searchPort = searchPortRef.current;
    if (currentResourceType === "包车") {
      const normalizedKeyword = keyword.trim();
      if (prefix !== "@" || !searchPort) {
        setMentionCandidates([]);
        setMentionOptions([]);
        return;
      }
      const searchKey = [currentResourceType, "sku", normalizedKeyword].join(":");
      if (searchKeyRef.current === searchKey) return;
      searchKeyRef.current = searchKey;
      const generation = ++searchGenerationRef.current;
      setMentionError("");
      try {
        const candidates = await searchPort("包车", "sku", normalizedKeyword, null);
        if (
          generation !== searchGenerationRef.current
          || resourceTypeRef.current !== currentResourceType
          || searchKeyRef.current !== searchKey
          || searchPortRef.current !== searchPort
        ) return;
        const deduplicated = [...new Map(candidates
          .filter((candidate) => candidate.resourceType === "包车")
          .map((candidate) => [
            candidate.vehicleSpecName ?? candidate.skuName ?? candidate.mentionText,
            candidate,
          ])).values()];
        for (const candidate of deduplicated) {
          candidateCacheRef.current.set(candidateCacheKey(candidate), candidate);
        }
        setMentionCandidates(deduplicated);
        setMentionOptions(deduplicated.map((candidate) => {
          const vehicleSpecName = candidate.vehicleSpecName
            ?? candidate.skuName
            ?? candidate.mentionText;
          return { value: vehicleSpecName, label: vehicleSpecName };
        }));
      } catch {
        if (
          generation !== searchGenerationRef.current
          || resourceTypeRef.current !== currentResourceType
          || searchKeyRef.current !== searchKey
          || searchPortRef.current !== searchPort
        ) return;
        searchKeyRef.current = null;
        setMentionCandidates([]);
        setMentionOptions([]);
        setMentionError("车型候选查询失败，请稍后重试");
      }
      return;
    }
    if (!isMentionResourceType(currentResourceType)) return;
    const textarea = mentionsRef.current?.querySelector("textarea");
    const caret = textarea?.selectionStart ?? textRef.current.length;
    const textBeforeCaret = textRef.current.slice(0, Math.max(0, caret));
    const currentTransportMode = transportModeFromText(textBeforeCaret);
    if (currentResourceType === "大交通" && currentTransportMode && prefix === "@"
      && transportPointsPortRef.current) {
      const pointsPort = transportPointsPortRef.current;
      const searchKey = [currentResourceType, "point", currentTransportMode, keyword.trim()].join(":");
      if (searchKeyRef.current === searchKey) return;
      searchKeyRef.current = searchKey;
      const generation = ++searchGenerationRef.current;
      setMentionError("");
      try {
        const points = await pointsPort(currentTransportMode, keyword);
        const trimmedKeyword = keyword.trim();
        const scopedPoints = points.filter((point) => (
          point.name.includes(trimmedKeyword) || point.shortName?.includes(trimmedKeyword)
        ));
        for (const point of scopedPoints) {
          transportPointCacheRef.current.set(`${currentTransportMode}:${point.name}`, {
            ...point,
            mode: currentTransportMode,
          });
        }
        if (
          generation !== searchGenerationRef.current
          || resourceTypeRef.current !== currentResourceType
          || searchKeyRef.current !== searchKey
          || transportPointsPortRef.current !== pointsPort
        ) return;
        setMentionCandidates([]);
        setMentionOptions(scopedPoints.map((point) => ({
          value: point.name,
          label: <span>{point.name}{point.city ? ` · ${point.city}` : ""}</span>,
        })));
      } catch {
        if (
          generation !== searchGenerationRef.current
          || resourceTypeRef.current !== currentResourceType
          || searchKeyRef.current !== searchKey
          || transportPointsPortRef.current !== pointsPort
        ) return;
        searchKeyRef.current = null;
        setMentionOptions([]);
        setMentionError("交通点位候选查询失败，请稍后重试");
      }
      return;
    }
    if (currentResourceType === "大交通" && currentTransportMode && prefix === "#") {
      setMentionOptions([]);
      return;
    }
    if (!searchPort) return;
    const cachedCandidates = [...candidateCacheRef.current.values()];
    const pointSpu = currentResourceType === "考察点" && prefix === "#"
      ? latestMentionedSpu(
        textRef.current.slice(0, Math.max(0, caret)),
        currentResourceType,
        cachedCandidates,
      )
      : null;
    if (currentResourceType === "考察点" && prefix === "#" && !pointSpu) {
      setMentionOptions([]);
      setMentionError("请先通过 @ 选择考察点，再通过 # 选择项目规格");
      return;
    }
    const inferredTarget = pointSpu
      ? { level: "sku" as const, spuId: pointSpu.spuId, transportMode: null }
      : inferMentionSearchTarget(
        currentResourceType,
        textRef.current,
        caret,
        cachedCandidates,
      );
    const target = currentResourceType === "住宿"
      ? { level: "sku" as const, spuId: null, transportMode: null }
      : inferredTarget;
    const searchKey = [
      currentResourceType,
      target.level,
      target.spuId ?? "",
      target.transportMode ?? "",
      keyword.trim(),
    ].join(":");
    if (searchKeyRef.current === searchKey) return;
    searchKeyRef.current = searchKey;
    const generation = ++searchGenerationRef.current;
    setMentionError("");
    try {
      const candidates = target.transportMode
        ? await searchPort(
          currentResourceType,
          target.level,
          keyword,
          target.spuId,
          target.transportMode,
        )
        : await searchPort(currentResourceType, target.level, keyword, target.spuId);
      if (
        generation !== searchGenerationRef.current
        || resourceTypeRef.current !== currentResourceType
        || searchKeyRef.current !== searchKey
        || searchPortRef.current !== searchPort
      ) return;
      const scopedCandidates = candidates.filter((candidate) => (
        candidate.resourceType === currentResourceType
        && candidate.level === target.level
        && (target.level === "spu" || target.spuId === null || candidate.spuId === target.spuId)
        && (!target.transportMode || candidate.transportMode === target.transportMode)
      ));
      let mergedCandidates = scopedCandidates;
      if (currentResourceType === "住宿") {
        mergedCandidates = [...new Map(mergedCandidates
          .filter((candidate) => candidate.level === "sku" && Boolean(candidate.skuName))
          .map((candidate) => [candidate.skuName!, candidate])).values()];
      }
      for (const candidate of mergedCandidates) {
        candidateCacheRef.current.set(candidateCacheKey(candidate), candidate);
      }
      setMentionCandidates(mergedCandidates);
      setMentionOptions(mergedCandidates.map((candidate) => ({
        value: currentResourceType === "住宿"
          ? candidate.skuName ?? candidate.mentionText
          : candidate.mentionText,
        label: currentResourceType === "住宿"
          ? candidate.skuName ?? candidate.mentionText
          : (
              <span>
                {candidate.mentionText}
                {candidate.description ? ` · ${candidate.description}` : ""}
              </span>
            ),
      })));
    } catch {
      if (
        generation !== searchGenerationRef.current
        || resourceTypeRef.current !== currentResourceType
        || searchKeyRef.current !== searchKey
        || searchPortRef.current !== searchPort
      ) return;
      searchKeyRef.current = null;
      setMentionCandidates([]);
      setMentionOptions([]);
      setMentionError("资源候选查询失败，请稍后重试");
    }
  };

  const scheduleMentionSearch = (keyword: string, prefix = "@") => {
    if (mentionSearchDebounceRef.current !== null) {
      window.clearTimeout(mentionSearchDebounceRef.current);
    }
    mentionSearchDebounceRef.current = window.setTimeout(() => {
      mentionSearchDebounceRef.current = null;
      void handleMentionSearch(keyword, prefix);
    }, 500);
  };

  const handleExampleClick = async (example: string) => {
    if (textRef.current.trim() && textRef.current !== example
      && !window.confirm("使用示例将替换当前分类的录入文本，确认继续吗？")) return;
    const exampleResourceType = resourceTypeRef.current;
    const exampleGeneration = ++exampleGenerationRef.current;
    const generation = ++searchGenerationRef.current;
    textRef.current = example;
    setDrafts((current) => ({ ...current, [exampleResourceType]: example }));
    setPreview([]);
    setError("");
    setMentionError("");
    setMentionCandidates([]);
    setMentionOptions([]);
    setExampleResolving(true);
    for (const [key, candidate] of candidateCacheRef.current) {
      if (candidate.resourceType === exampleResourceType) candidateCacheRef.current.delete(key);
    }
    if (exampleResourceType === "大交通") {
      transportPointCacheRef.current.clear();
    }

    const isCurrent = () => (
      generation === searchGenerationRef.current
      && resourceTypeRef.current === exampleResourceType
      && textRef.current === example
    );
    const fail = (message: string) => {
      if (!isCurrent()) return;
      setPreview([]);
      setError("");
      setMentionError(message);
    };
    const uniqueCandidate = <T extends { id?: string; skuId?: string | null; spuId?: string | null }>(
      values: readonly T[],
    ): T | null => {
      const unique = [...new Map(values.map((item) => [
        item.id ?? item.skuId ?? item.spuId ?? JSON.stringify(item),
        item,
      ])).values()];
      return unique.length === 1 ? unique[0]! : null;
    };
    const replaceMarker = (source: string, prefix: "@" | "#", from: string, to: string) => (
      source.split(`${prefix}${from}`).join(`${prefix}${to}`)
    );

    try {
      if (exampleResourceType === "包车") {
        const searchPort = searchPortRef.current;
        if (!searchPort) {
          fail("车型候选查询不可用，请稍后重试");
          return;
        }
        const vehicleSpecs = [...new Set(
          [...example.matchAll(/@(\S+?)(?=\s|[,，]|$)/gu)]
            .map((match) => match[1]?.trim())
            .filter((value): value is string => Boolean(value)),
        )];
        for (const vehicleSpec of vehicleSpecs) {
          const candidates = await searchPort("包车", "sku", vehicleSpec, null);
          if (!isCurrent()) return;
          const matched = candidates.find((candidate) => (
            (candidate.vehicleSpecName ?? candidate.skuName ?? candidate.mentionText)
              === vehicleSpec
          ));
          if (!matched) {
            fail(`当前没有启用的车型“${vehicleSpec}”，请从候选中重新选择`);
            return;
          }
          candidateCacheRef.current.set(candidateCacheKey(matched), matched);
        }
        if (!vehicleSpecs.length) {
          fail("包车示例没有可关联的车型，请检查示例格式");
          return;
        }
        if (!isCurrent()) return;
        updateDraftAndPreview(example);
        return;
      }

      if (exampleResourceType === "住宿") {
        const searchPort = searchPortRef.current;
        const staySpuId = selectedStaySpuRef.current?.spuId?.trim();
        if (!staySpuId) {
          fail("请先选择住宿酒店，再点击住宿示例");
          return;
        }
        if (!searchPort) {
          fail("住宿资源候选查询不可用，请稍后重试");
          return;
        }
        const roomNames = [...new Set(
          [...example.matchAll(/@([^@;；,，\r\n]+)/gu)]
            .map((match) => match[1]?.trim().match(
              /^([^\d\s]+?)(?=住(?:两人|学生|老师|教师|导游|司机)|\s|\d|$)/u,
            )?.[1])
            .filter((value): value is string => Boolean(value)),
        )];
        let resolvedText = example;
        for (const roomName of roomNames) {
          const candidates = await searchPort(
            "住宿",
            "sku",
            roomName,
            null,
          );
          if (!isCurrent()) return;
          const fuzzyCandidates = candidates.filter((candidate) => (
            candidate.resourceType === "住宿"
            && candidate.level === "sku"
            && candidate.skuName?.includes(roomName)
          ));
          const exactCandidates = fuzzyCandidates.filter(
            (candidate) => candidate.skuName === roomName,
          );
          const matchingCandidates = exactCandidates.length > 0
            ? exactCandidates
            : fuzzyCandidates;
          const matched = [...new Map(matchingCandidates.map((candidate) => [
            candidate.skuName,
            candidate,
          ])).values()][0] ?? null;
          if (!matched) {
            fail(`当前没有启用的房型“${roomName}”，请从候选中明确选择`);
            return;
          }
          candidateCacheRef.current.set(candidateCacheKey(matched), matched);
          resolvedText = replaceMarker(resolvedText, "@", roomName, matched.mentionText);
        }
        if (!roomNames.length) {
          fail("住宿示例没有可关联的房型，请检查示例格式");
          return;
        }
        if (!isCurrent()) return;
        textRef.current = resolvedText;
        setMentionError("");
        updateDraftAndPreview(resolvedText);
        return;
      }

      if (exampleResourceType === "考察点") {
        const searchPort = searchPortRef.current;
        if (!searchPort) {
          fail("考察点资源候选查询不可用，请稍后重试");
          return;
        }
        if (!example.includes("#") && pointGlobalSkuNames(example).length > 0) {
          pointResourceSearchKeyRef.current = null;
          await ensurePointGlobalSkuCandidates(example);
          return;
        }
        const mentions = [...example.matchAll(/([@#])([^@#;；,，\r\n]+?)(?=\s|\d|$)/gu)]
          .map((match) => ({ prefix: match[1] as "@" | "#", name: match[2]!.trim() }));
        const [pointMention, ...skuMentions] = mentions;
        if (!pointMention || pointMention.prefix !== "@" || !skuMentions.length) {
          fail("考察点示例必须包含 @考察点 和 #项目规格");
          return;
        }
        const point = uniqueCandidate((await searchPort(
          "考察点",
          "spu",
          pointMention.name,
          null,
        )).filter((candidate) => (
          candidate.resourceType === "考察点"
          && candidate.level === "spu"
          && candidate.spuName === pointMention.name
        )));
        if (!isCurrent()) return;
        if (!point) {
          fail(`未能唯一关联考察点“${pointMention.name}”，请从候选中重新选择`);
          return;
        }
        candidateCacheRef.current.set(candidateCacheKey(point), point);
        let resolvedText = replaceMarker(example, "@", pointMention.name, point.mentionText);
        for (const skuMention of skuMentions) {
          const sku = uniqueCandidate((await searchPort(
            "考察点",
            "sku",
            skuMention.name,
            point.spuId,
          )).filter((candidate) => (
            candidate.resourceType === "考察点"
            && candidate.level === "sku"
            && candidate.spuId === point.spuId
            && candidate.skuName === skuMention.name
          )));
          if (!isCurrent()) return;
          if (!sku) {
            fail(`未能在“${point.spuName}”下唯一关联项目“${skuMention.name}”，请从候选中重新选择`);
            return;
          }
          candidateCacheRef.current.set(candidateCacheKey(sku), sku);
          resolvedText = replaceMarker(resolvedText, skuMention.prefix, skuMention.name, sku.mentionText);
        }
        if (!isCurrent()) return;
        textRef.current = resolvedText;
        setMentionError("");
        updateDraftAndPreview(resolvedText);
        return;
      }

      if (exampleResourceType === "大交通") {
        const pointsPort = transportPointsPortRef.current;
        if (!pointsPort) {
          fail("大交通资源候选查询不可用，请稍后重试");
          return;
        }
        const contexts = transportMentionContexts(example);
        if (!contexts.length) {
          fail("大交通示例必须包含方式、起点和终点");
          return;
        }
        for (const context of contexts) {
          const fromCandidates = (await pointsPort(context.mode, context.fromName)).filter(
            (point) => point.name === context.fromName || point.shortName === context.fromName,
          );
          if (!isCurrent()) return;
          const toCandidates = (await pointsPort(context.mode, context.toName)).filter(
            (point) => point.name === context.toName || point.shortName === context.toName,
          );
          if (!isCurrent()) return;
          const from = uniqueCandidate(fromCandidates);
          const to = uniqueCandidate(toCandidates);
          if (!from || !to) {
            fail(transportPointMatchError(
              context.mode,
              !from ? context.fromName : context.toName,
              !from ? fromCandidates.length : toCandidates.length,
            ));
            return;
          }
          transportPointCacheRef.current.set(`${context.mode}:${from.name}`, { ...from, mode: context.mode });
          transportPointCacheRef.current.set(`${context.mode}:${to.name}`, { ...to, mode: context.mode });

        }
        if (!isCurrent()) return;
        transportPointSearchKeyRef.current = contexts.map((context) => (
          [context.mode, context.fromName, context.toName, context.seatClass ?? ""].join(":")
        )).join("|");
        setMentionError("");
        updateDraftAndPreview(example);
        return;
      }

      handleTextChange(example);
    } catch {
      fail("示例资源关联失败，请稍后重试或从候选中手工选择");
    } finally {
      if (exampleGeneration === exampleGenerationRef.current) setExampleResolving(false);
    }
  };

  const activateResourceType = (
    nextType: ConfirmationEntryResourceType,
    shouldFocus = false,
  ) => {
    resourceTypeRef.current = nextType;
    textRef.current = drafts[nextType];
    exampleGenerationRef.current += 1;
    searchGenerationRef.current += 1;
    searchKeyRef.current = null;
    setResourceType(nextType);
    setPreview([]);
    setError("");
    setMentionError("");
    setMentionCandidates([]);
    setMentionOptions([]);
    setExampleResolving(false);
    transportPointSearchKeyRef.current = null;
    pointResourceSearchKeyRef.current = null;
    busVehicleSpecSearchKeyRef.current = null;
    if (mentionSearchDebounceRef.current !== null) {
      window.clearTimeout(mentionSearchDebounceRef.current);
      mentionSearchDebounceRef.current = null;
    }
    if (resourceResolutionDebounceRef.current !== null) {
      window.clearTimeout(resourceResolutionDebounceRef.current);
      resourceResolutionDebounceRef.current = null;
    }
    onResourceTypeChange?.(nextType);
    if (shouldFocus) {
      tabRefs.current[VISIBLE_CONFIRMATION_ENTRY_RESOURCE_TYPES.indexOf(
        nextType as (typeof VISIBLE_CONFIRMATION_ENTRY_RESOURCE_TYPES)[number],
      )]?.focus();
    }
  };
  const handleTabKeyDown = (
    event: KeyboardEvent<HTMLButtonElement>,
    index: number,
  ) => {
    const optionCount = VISIBLE_CONFIRMATION_ENTRY_RESOURCE_TYPES.length;
    let targetIndex: number;
    switch (event.key) {
      case "ArrowRight":
      case "ArrowDown":
        targetIndex = (index + 1) % optionCount;
        break;
      case "ArrowLeft":
      case "ArrowUp":
        targetIndex = (index - 1 + optionCount) % optionCount;
        break;
      case "Home":
        targetIndex = 0;
        break;
      case "End":
        targetIndex = optionCount - 1;
        break;
      default:
        return;
    }
    event.preventDefault();
    activateResourceType(VISIBLE_CONFIRMATION_ENTRY_RESOURCE_TYPES[targetIndex]!, true);
  };

  const restorePreviewRef = useRef<() => void>(() => undefined);
  restorePreviewRef.current = () => {
    updateDraftAndPreview(textRef.current);
    void ensureBusVehicleSpecCandidates(textRef.current);
    void ensureStayRoomSkuCandidates(textRef.current);
    void ensureTransportPoints(textRef.current);
    void ensurePointGlobalSkuCandidates(textRef.current);
  };
  useEffect(() => {
    if (!suspended) restorePreviewRef.current();
  }, [resourceType, suspended]);

  if (suspended) return null;

  return (
    <section className="fulfillment-order-quick-entry" aria-label="确认行快速录入">
      <div className="fulfillment-order-entry-tabs" role="tablist" aria-label="确认件录入分类">
        {VISIBLE_CONFIRMATION_ENTRY_RESOURCE_TYPES.map((type, index) => (
          <button key={type} type="button" role="tab" aria-selected={resourceType === type}
            className={resourceType === type ? "is-active" : ""}
            tabIndex={resourceType === type ? 0 : -1}
            ref={(element) => {
              tabRefs.current[index] = element;
            }}
            onClick={() => activateResourceType(type)}
            onKeyDown={(event) => handleTabKeyDown(event, index)}>
            {type}
          </button>
        ))}
      </div>
      <div className="fulfillment-order-entry-heading">
        <div className="fulfillment-order-entry-heading-copy">
          <strong>{resourceType}录入</strong>
          <span>按出行天、项目、数量和单价录入，输入后自动识别。</span>
        </div>
        <div className="fulfillment-order-entry-heading-actions">
          {preview.length ? <span>共 {preview.length} 条 · ¥{previewTotal.toFixed(2)}</span> : null}
          <Button
            type="primary"
            disabled={disabled || exampleResolving || preview.length === 0}
            onClick={() => { void handleAddPreview(); }}
          >
            加入明细
          </Button>
        </div>
      </div>
      {subjectSlot}
      <div className="fulfillment-order-entry-grid">
        <div>
          {usesMentionsInput(resourceType) ? (
            <Mentions
              ref={mentionsRef}
              aria-label="确认件录入内容"
              value={text}
              disabled={disabled}
              rows={5}
              prefix={resourceType === "包车" ? ["@"] : ["@", "#"]}
              split=" "
              alignTextarea={false}
              filterOption={false}
              options={mentionOptions.map((option) => ({
                ...option,
                label: (
                  <span onPointerDown={() => { mentionOptionSelectingRef.current = true; }}>
                    {option.label}
                  </span>
                ),
              }))}
              notFoundContent={resourceType === "包车" ? "暂无匹配车型" : "暂无匹配资源"}
              placeholder={`例如：${EXAMPLES[resourceType][0]}`}
              onSearch={scheduleMentionSearch}
              onChange={handleTextChange}
              onKeyDownCapture={handleEntryKeyDownCapture}
              onCompositionStart={() => { mentionPhysicalInputRef.current = true; }}
              onCompositionEnd={() => {
                mentionPhysicalInputRef.current = false;
                scheduleMentionKeyupFallback();
              }}
            />
          ) : (
            <Input.TextArea
              aria-label="确认件录入内容"
              value={text}
              disabled={disabled}
              rows={5}
              placeholder={`例如：${EXAMPLES[resourceType][0]}`}
              onChange={handleTextChange}
            />
          )}
          {error || mentionError ? (
            <div className="fulfillment-order-entry-error" role="alert">
              {mentionError || error}
            </div>
          ) : null}
        </div>
        <aside className="fulfillment-order-entry-preview" aria-label="识别预览">
          <strong>识别预览</strong>
          {preview.length ? preview.map((entry, index) => (
            <div key={`${entry.sourceText}-${index}`} className="fulfillment-order-entry-preview-row">
              <span>{entry.dayExpression} · {entry.itemName}</span>
              <b>¥{entry.amount}</b>
              {entry.needsDateAdjustment ? <Tag color="orange">日期待调整</Tag> : null}
            </div>
          )) : <span>输入后将自动识别，核对无误再加入明细。</span>}
        </aside>
      </div>
      {afterEntrySlot}
      <div className="fulfillment-order-entry-examples" aria-label={`${resourceType}录入示例`}>
        {EXAMPLES[resourceType].map((example) => (
          <button key={example} type="button" disabled={disabled || exampleResolving}
            aria-label={`示例：${example}`} onClick={() => {
              void handleExampleClick(example);
            }}>
            {example}
          </button>
        ))}
      </div>
    </section>
  );
}
